import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getApiConnectionErrorMessage } from '../utils/apiErrorHints';
import { useAuth } from '../context/AuthContext';
import './AuthPages.css';

export default function OTPPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
    }, 1000);
  };

  const sendOTP = async () => {
    if (!email) return toast.error('Enter your email');
    setLoading(true);
    try {
      await api.post('/auth/send-otp', { email });
      setStep(2);
      toast.success('OTP sent! Check your email');
      startCountdown();
    } catch (err) {
      const net =
        err.code === 'ERR_NETWORK' ||
        err.message === 'Network Error' ||
        !err.response;
      toast.error(
        err.response?.data?.message ||
          (net ? getApiConnectionErrorMessage() : 'Failed to send OTP')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyOTP = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) return toast.error('Enter complete 6-digit OTP');
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', { email, otp: otpString });
      login(res.data.token, res.data.user);
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired OTP');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-bg-glow" />
      <div className="auth-card">
        <Link to="/" className="auth-brand">⚡ Zip2Extract <span>AI</span></Link>
        {step === 1 ? (
          <>
            <h2>Login with OTP</h2>
            <p className="auth-sub">Get a one-time password sent to your email</p>
            <div className="auth-form">
              <div className="input-group">
                <Mail size={16} className="input-icon" />
                <input className="input" type="email" placeholder="Enter your email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendOTP()} />
              </div>
              <button className="btn btn-primary w-full" onClick={sendOTP} disabled={loading}>
                {loading ? 'Sending OTP...' : <>Send OTP <ArrowRight size={16} /></>}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Enter OTP</h2>
            <p className="auth-sub">6-digit code sent to <strong>{email}</strong></p>
            <div className="otp-boxes">
              {otp.map((digit, i) => (
                <input key={i} ref={el => inputRefs.current[i] = el} className="otp-box" type="text"
                  inputMode="numeric" maxLength={1} value={digit}
                  onChange={e => handleOTPChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)} />
              ))}
            </div>
            <button className="btn btn-primary w-full" onClick={verifyOTP} disabled={loading} style={{ marginTop: '24px' }}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <div className="otp-resend">
              {countdown > 0 ? (
                <p>Resend in <strong>{countdown}s</strong></p>
              ) : (
                <button className="link-btn" onClick={sendOTP}>Resend OTP</button>
              )}
            </div>
            <button className="link-btn" onClick={() => setStep(1)}>← Change email</button>
          </>
        )}
        <p className="auth-footer">
          Have a password? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
